// Pure helpers for selecting which budget document(s) to show in Budget Review.
//
// Kept intentionally free of React / redux / MUI imports so the selection logic
// can be unit-tested in isolation (and so a bug here is cheap to cover with a
// regression test).
//
// Background: these docs come from the tagged-documents endpoint, which returns
// EVERY document mapped to the loan's property (BPO/BOV, AVM, HUD, budget, ...).
// The Budget Review page only ever wants the budget document (tag REBD/SCOW).

type DocTag = { tag?: { code?: string } };
type DocAsset = { assetName?: string; assetExternalId?: string };

export interface BudgetDocLike {
  isLatest?: boolean;
  isPurchasePrimary?: boolean;
  TagsInternal?: DocTag[];
  assets?: DocAsset[];
  // Allow the full document shape through untouched.
  [key: string]: any;
}

export const BUDGET_TAG_CODES = ["REBD", "SCOW"];

export function hasBudgetTag(doc: BudgetDocLike): boolean {
  const tags = doc?.TagsInternal || [];
  for (let j = 0; j < tags.length; j++) {
    const tagCode = tags[j]?.tag?.code;
    if (tagCode && BUDGET_TAG_CODES.indexOf(tagCode) !== -1) {
      return true;
    }
  }
  return false;
}

/**
 * Among the documents for a SINGLE property, prefer the latest version(s).
 * If none are flagged `isLatest`, fall back to the purchase-primary version(s).
 *
 * NOTE: this must only ever be handed budget documents. Grouping across
 * document TYPES lets a newer non-budget doc (e.g. a re-ingested BPO with
 * `isLatest: true`) win the "latest" contest for the property and drop the
 * budget doc. Callers should filter by budget tag first (see filterObjectsByTag).
 */
export function filterDocumentsGroupedByProperty(
  docs: BudgetDocLike[]
): BudgetDocLike[] {
  const grouped: Record<string, BudgetDocLike[]> = {};

  // Step 1: Group by assetExternalId where assetName === "PROPERTY"
  for (const doc of docs) {
    const propertyAsset = (doc.assets || []).find(
      (asset) => asset.assetName === "PROPERTY"
    );
    if (propertyAsset) {
      const key = propertyAsset.assetExternalId as string;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(doc);
    }
  }

  // Step 2: For each group, apply filtering rules
  const filtered: BudgetDocLike[] = [];
  for (const groupDocs of Object.values(grouped)) {
    const latestDocs = groupDocs.filter((doc) => doc.isLatest);
    if (latestDocs.length > 0) {
      filtered.push(...latestDocs);
    } else {
      const primaryDocs = groupDocs.filter((doc) => doc.isPurchasePrimary);
      filtered.push(...primaryDocs);
    }
  }

  return filtered;
}

/**
 * Returns the budget document(s) (tag REBD/SCOW) for the loan.
 *
 * The budget-tag filter runs BEFORE the per-property latest/primary selection.
 * Doing it the other way around lets an unrelated newer document that shares the
 * same PROPERTY asset (e.g. a BPO/BOV re-ingested with `isLatest: true`)
 * suppress the budget doc, which surfaces as "No Document Found" on the Budget
 * Review v2 page even though the budget document exists.
 */
export function filterObjectsByTag(docsList: BudgetDocLike[]): BudgetDocLike[] {
  const budgetDocs = (docsList || []).filter(hasBudgetTag);
  return filterDocumentsGroupedByProperty(budgetDocs);
}
